import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sun, Moon, Monitor } from 'lucide-react';

export default function PreferencesPage() {
  const { theme, setTheme } = useTheme();

  const [appearance, setAppearance] = useState({
    colorScheme: 'blue',
    sidebarCollapsed: false,
    compactMode: false,
    animations: true,
    glassmorphism: true,
  });

  const [workspace, setWorkspace] = useState({
    defaultView: 'dashboard',
    autoSaveInterval: '30',
    showQuickActions: true,
    showRecentItems: true,
    itemsPerPage: '20',
    defaultTimeView: 'week',
  });

  const [productivity, setProductivity] = useState({
    pomodoroLength: '25',
    shortBreak: '5',
    longBreak: '15',
    autoStartBreaks: false,
    autoStartPomodoros: false,
    enableSounds: true,
    dailyGoal: '8',
    weeklyGoal: '40',
    remindersEnabled: true,
    focusMode: false,
  });

  const [dataSync, setDataSync] = useState({
    autoBackup: true,
    backupFrequency: 'daily',
    cloudSync: true,
    offlineMode: false,
    dataRetention: '365',
    exportFormat: 'json',
  });

  const handleAppearanceChange = (key: string, value: string | boolean) => {
    setAppearance((prev) => ({ ...prev, [key]: value }));
  };

  const handleWorkspaceChange = (key: string, value: string | boolean) => {
    setWorkspace((prev) => ({ ...prev, [key]: value }));
  };

  const handleProductivityChange = (key: string, value: string | boolean) => {
    setProductivity((prev) => ({ ...prev, [key]: value }));
  };

  const handleDataSyncChange = (key: string, value: string | boolean) => {
    setDataSync((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    console.log('Saving preferences:', {
      appearance,
      workspace,
      productivity,
      dataSync,
    });
  };

  const resetToDefaults = () => {
    if (confirm('Are you sure you want to reset all preferences to defaults?')) {
      setTheme('system');
      setAppearance({
        colorScheme: 'blue',
        sidebarCollapsed: false,
        compactMode: false,
        animations: true,
        glassmorphism: true,
      });
      setWorkspace({
        defaultView: 'dashboard',
        autoSaveInterval: '30',
        showQuickActions: true,
        showRecentItems: true,
        itemsPerPage: '20',
        defaultTimeView: 'week',
      });
      setProductivity({
        pomodoroLength: '25',
        shortBreak: '5',
        longBreak: '15',
        autoStartBreaks: false,
        autoStartPomodoros: false,
        enableSounds: true,
        dailyGoal: '8',
        weeklyGoal: '40',
        remindersEnabled: true,
        focusMode: false,
      });
      setDataSync({
        autoBackup: true,
        backupFrequency: 'daily',
        cloudSync: true,
        offlineMode: false,
        dataRetention: '365',
        exportFormat: 'json',
      });
    }
  };

  return (
    <AppLayout>
      <Breadcrumb
        items={[{ label: 'Settings', href: '/settings' }, { label: 'App Preferences' }]}
      />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">App Preferences</h1>
            <p className="text-muted-foreground">Customize your workspace and app behavior</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetToDefaults}>
              Reset to Defaults
            </Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Appearance */}
          <Card className="border border-border rounded-lg p-4 border-0">
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Theme Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Theme</Label>
                <div className="flex gap-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('light')}
                    className="flex-1"
                  >
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('dark')}
                    className="flex-1"
                  >
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </Button>
                  <Button
                    variant={theme === 'system' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('system')}
                    className="flex-1"
                  >
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                  </Button>
                </div>
              </div>

              {/* Color Scheme */}
              <div className="space-y-2">
                <Label>Color Scheme</Label>
                <Select
                  value={appearance.colorScheme}
                  onValueChange={(value) => handleAppearanceChange('colorScheme', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select color scheme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="indigo">Indigo</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="red">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Toggle Settings */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Sidebar Collapsed by Default</Label>
                    <p className="text-sm text-muted-foreground">
                      Start with a collapsed sidebar for more space
                    </p>
                  </div>
                  <Switch
                    checked={appearance.sidebarCollapsed}
                    onCheckedChange={(checked) =>
                      handleAppearanceChange('sidebarCollapsed', checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Compact Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Reduce padding and spacing for a denser layout
                    </p>
                  </div>
                  <Switch
                    checked={appearance.compactMode}
                    onCheckedChange={(checked) =>
                      handleAppearanceChange('compactMode', checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Animations</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable smooth transitions and animations
                    </p>
                  </div>
                  <Switch
                    checked={appearance.animations}
                    onCheckedChange={(checked) =>
                      handleAppearanceChange('animations', checked)
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Glassmorphism Effects</Label>
                    <p className="text-sm text-muted-foreground">
                      Modern glass-like UI effects
                    </p>
                  </div>
                  <Switch
                    checked={appearance.glassmorphism}
                    onCheckedChange={(checked) =>
                      handleAppearanceChange('glassmorphism', checked)
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workspace */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Workspace</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Default View</label>
                <select
                  value={workspace.defaultView}
                  onChange={(e) => handleWorkspaceChange('defaultView', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="time-tracker">Time Tracker</option>
                  <option value="tasks">Tasks</option>
                  <option value="clients">Clients</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto-save Interval
                </label>
                <select
                  value={workspace.autoSaveInterval}
                  onChange={(e) => handleWorkspaceChange('autoSaveInterval', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="10">10 seconds</option>
                  <option value="30">30 seconds</option>
                  <option value="60">1 minute</option>
                  <option value="300">5 minutes</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Items Per Page
                </label>
                <select
                  value={workspace.itemsPerPage}
                  onChange={(e) => handleWorkspaceChange('itemsPerPage', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Time View
                </label>
                <select
                  value={workspace.defaultTimeView}
                  onChange={(e) => handleWorkspaceChange('defaultTimeView', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Show Quick Actions</div>
                  <div className="text-sm text-gray-600">
                    Display quick action buttons in the toolbar
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={workspace.showQuickActions}
                    onChange={(e) => handleWorkspaceChange('showQuickActions', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Show Recent Items</div>
                  <div className="text-sm text-gray-600">
                    Display recently accessed items in the sidebar
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={workspace.showRecentItems}
                    onChange={(e) => handleWorkspaceChange('showRecentItems', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Productivity */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Productivity</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pomodoro Length (minutes)
                </label>
                <input
                  type="number"
                  value={productivity.pomodoroLength}
                  onChange={(e) => handleProductivityChange('pomodoroLength', e.target.value)}
                  min="15"
                  max="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Break (minutes)
                </label>
                <input
                  type="number"
                  value={productivity.shortBreak}
                  onChange={(e) => handleProductivityChange('shortBreak', e.target.value)}
                  min="3"
                  max="15"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Long Break (minutes)
                </label>
                <input
                  type="number"
                  value={productivity.longBreak}
                  onChange={(e) => handleProductivityChange('longBreak', e.target.value)}
                  min="10"
                  max="30"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Goal (hours)
                </label>
                <input
                  type="number"
                  value={productivity.dailyGoal}
                  onChange={(e) => handleProductivityChange('dailyGoal', e.target.value)}
                  min="1"
                  max="16"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weekly Goal (hours)
                </label>
                <input
                  type="number"
                  value={productivity.weeklyGoal}
                  onChange={(e) => handleProductivityChange('weeklyGoal', e.target.value)}
                  min="5"
                  max="80"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Auto-start Breaks</div>
                  <div className="text-sm text-gray-600">
                    Automatically start breaks after pomodoros
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productivity.autoStartBreaks}
                    onChange={(e) => handleProductivityChange('autoStartBreaks', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Enable Timer Sounds</div>
                  <div className="text-sm text-gray-600">Play sounds when timers start and end</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productivity.enableSounds}
                    onChange={(e) => handleProductivityChange('enableSounds', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Focus Mode</div>
                  <div className="text-sm text-gray-600">
                    Hide distracting elements during active timers
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productivity.focusMode}
                    onChange={(e) => handleProductivityChange('focusMode', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Data & Sync */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Data & Sync</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Backup Frequency
                </label>
                <select
                  value={dataSync.backupFrequency}
                  onChange={(e) => handleDataSyncChange('backupFrequency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="manual">Manual Only</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Retention (days)
                </label>
                <select
                  value={dataSync.dataRetention}
                  onChange={(e) => handleDataSyncChange('dataRetention', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                  <option value="730">2 years</option>
                  <option value="unlimited">Unlimited</option>
                </select>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Auto Backup</div>
                  <div className="text-sm text-gray-600">Automatically backup your data</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataSync.autoBackup}
                    onChange={(e) => handleDataSyncChange('autoBackup', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Cloud Sync</div>
                  <div className="text-sm text-gray-600">Sync data across devices</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataSync.cloudSync}
                    onChange={(e) => handleDataSyncChange('cloudSync', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Offline Mode</div>
                  <div className="text-sm text-gray-600">Enable offline functionality</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dataSync.offlineMode}
                    onChange={(e) => handleDataSyncChange('offlineMode', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
